import { ChangeDetectionStrategy, Component, ViewEncapsulation, booleanAttribute, computed, input, model } from '@angular/core';

export const DS_CARD_SURFACE_ARRAY = ['lowest', 'low', 'high', 'highest'] as const;
export type DsCardSurface = (typeof DS_CARD_SURFACE_ARRAY)[number];

/** @deprecated Use DS_CARD_SURFACE_ARRAY instead. */
export const DS_CARD_VARIANT_ARRAY = ['surface-lowest', 'surface-low', 'surface-high', 'surface-highest'] as const;
/** @deprecated Use DsCardSurface instead. */
export type DsCardVariant = (typeof DS_CARD_VARIANT_ARRAY)[number];

@Component({
    selector: 'ds-card',
    template: `<ng-content />`,
    host: {
        'class': 'ds-card',
        '[class]': 'hostClass()',
        '[class.ds-card-clickable]': 'clickableValue()',
        '[class.ds-card-elevated]': 'elevated()',
        '[class.ds-card-border]': 'borderValue()',
        '[class.ds-card-radius]': 'radiusValue()',
        '[class.ds-card-inverse]': 'inverse()',
    },
    styleUrl: 'card.component.scss',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class DsCard {
    /** ------------------ New API ------------------ */
    public readonly surface = model<DsCardSurface>('lowest');
    public readonly inverse = model<boolean>(false);
    public readonly clickable = input(false, { transform: booleanAttribute });
    public readonly radius = model<boolean>(true);
    public readonly border = model<boolean>(false);
    /** @breaking Breaking: default changed from true → false */
    public readonly elevated = model<boolean>(false);

    /** ------------------ Legacy API ------------------ */
    /** @deprecated Use surface instead */
    public readonly variant = model<DsCardVariant>('surface-lowest');
    /** @deprecated Use clickable instead */
    public readonly withHover = input(false, { transform: booleanAttribute });
    /** @deprecated Use radius instead (inverted logic) */
    public readonly noBorderRadius = model<boolean | undefined>(undefined);
    /** @deprecated Use border instead (inverted logic) */
    public readonly noBorder = model<boolean | undefined>(undefined);

    /** ------------------ Computed host classes ------------------ */
    /** Surface class:
     *  - New API `surface` wins if explicitly set
     *  - Legacy `variant` used only if explicitly set to non-default
     */
    protected readonly hostClass = computed(() => {
        const legacy = this.variant();

        const surfaceValue: DsCardSurface = legacy
            ? legacy === 'surface-lowest'
                ? this.surface()
                : (legacy.replace('surface-', '') as DsCardSurface)
            : this.surface();

        return `ds-card-surface-${surfaceValue}`;
    });

    /** Clickable class:
     *  - True if either new API (`clickable`) or legacy (`withHover`) is true
     */
    protected readonly clickableValue = computed(() => this.clickable() || this.withHover());

    /** Border class:
     *  - Legacy `noBorder` explicitly disables/enables border
     *  - If legacy not set, fallback to new API `border` default
     */
    protected readonly borderValue = computed(() => {
        const legacy = this.noBorder();

        if (legacy === true) {
            return false;
        }

        if (legacy === false) {
            return true;
        }

        return this.border();
    });

    /** Radius class:
     *  - Legacy `noBorderRadius` explicitly disables/enables radius
     *  - If legacy not set, fallback to new API `radius` default
     */
    protected readonly radiusValue = computed(() => {
        const legacy = this.noBorderRadius();

        if (legacy === true) {
            return false;
        }

        if (legacy === false) {
            return true;
        }

        return this.radius();
    });
}
